import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, KeyRound, Loader2, Mail, Trash2, User } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '../ui/dialog';
import OfficeModalHeader from './OfficeModalHeader';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { API_BASE_URL } from '../../apiBase';
import { resolveUserDisplayName } from '../../lib/userDisplayName';
import { cn } from '../../lib/utils';

function roleLabel(role, t) {
  if (role === 'admin') return t('sidebar.role.admin');
  if (role === 'reseller') return t('sidebar.role.reseller');
  if (role === 'attendant') return t('sidebar.role.attendant');
  if (role === 'attendant' || role === 'agent') return t('account.role.attendant');
  return role || t('account.role.user');
}

export function userAvatarSrc(user) {
  const u = user?.avatar_url;
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  return `${API_BASE_URL}${u.startsWith('/') ? u : `/${u}`}`;
}

function initials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return 'A';
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

export function UserAvatar({ user, size = 'md', className }) {
  const src = userAvatarSrc(user);
  const [broken, setBroken] = useState(false);
  const dim = size === 'lg' ? 'h-20 w-20 text-xl' : size === 'sm' ? 'h-8 w-8 text-sm' : 'h-10 w-10 text-sm';
  const label = resolveUserDisplayName(user);

  useEffect(() => {
    setBroken(false);
  }, [src]);

  if (src && !broken) {
    return (
      <img
        src={src}
        alt={label}
        className={cn('office-avatar rounded-[3px] object-cover ring-2 ring-primary/20', dim, className)}
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <span
      className={cn(
        'office-avatar grid place-items-center rounded-[3px] bg-primary font-display font-bold text-primary-foreground ring-2 ring-primary/25',
        dim,
        className,
      )}
      aria-hidden
    >
      {initials(label)}
    </span>
  );
}

export function ProfileModal({ open, onOpenChange }) {
  const { user, checkAuth, applyAvatarSnapshot } = useAuth();
  const { t } = useLanguage();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [removing, setRemoving] = useState(false);

  const busy = uploading || importing || removing;

  const refresh = async (accountId, data) => {
    if (typeof applyAvatarSnapshot === 'function') applyAvatarSnapshot(accountId, data);
    else if (typeof checkAuth === 'function') await checkAuth();
  };

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Selecione uma imagem (JPG, PNG ou WEBP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_BASE_URL}/api/auth/avatar`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data?.message || 'Foto atualizada');
      await refresh(user?.id, data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao enviar a foto');
    } finally {
      setUploading(false);
    }
  };

  const importFromEmail = async () => {
    setImporting(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/auth/avatar/from-email`,
        {},
        { withCredentials: true },
      );
      toast.success(data?.message || 'Foto importada');
      await refresh(user?.id, data);
    } catch (err) {
      const status = err?.response?.status;
      const detail =
        err?.response?.data?.detail ||
        'Não há foto pública neste email. Envie uma imagem com «Enviar foto».';
      // 404 esperado (Gmail sem Gravatar): aviso informativo, não erro vermelho.
      if (status === 404) {
        toast.info(detail, { title: 'Sem foto pública' });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Falha ao importar a foto');
      }
    } finally {
      setImporting(false);
    }
  };

  const removeAvatar = async () => {
    setRemoving(true);
    try {
      const { data } = await axios.delete(`${API_BASE_URL}/api/auth/avatar`, {
        withCredentials: true,
      });
      toast.success(data?.message || 'Foto removida');
      await refresh(user?.id, data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao remover a foto');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="modal-perfil">
        <OfficeModalHeader
          icon={User}
          title={t('account.profileTitle')}
          description={t('account.profileHint')}
        />

        <div className="flex flex-col items-center gap-3 py-2">
          <div className="relative">
            <UserAvatar user={user} size="lg" />
            <button
              type="button"
              onClick={onPickFile}
              disabled={busy}
              className="absolute -bottom-1 -right-1 grid h-8 w-8 place-items-center rounded-[3px] border border-border bg-card text-foreground shadow-sm transition hover:bg-accent disabled:opacity-50"
              title={t('account.uploadPhoto')}
              aria-label={t('account.uploadPhoto')}
            >
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={onFileChange}
          />
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button type="button" variant="outline" size="sm" className="rounded-md" onClick={onPickFile} disabled={busy}>
              {uploading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Camera className="mr-1.5 h-3.5 w-3.5" />}
              {t('account.uploadPhoto')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-md"
              onClick={importFromEmail}
              disabled={busy}
              title={t('account.fromEmailHint')}
            >
              {importing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-1.5 h-3.5 w-3.5" />}
              {t('account.fromEmail')}
            </Button>
            {user?.has_avatar || user?.avatar_url ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-md text-destructive hover:text-destructive"
                onClick={removeAvatar}
                disabled={busy}
              >
                {removing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Trash2 className="mr-1.5 h-3.5 w-3.5" />}
                {t('common.remove')}
              </Button>
            ) : null}
          </div>
          <p className="max-w-sm text-center text-[11px] leading-relaxed text-muted-foreground">
            {t('account.avatarHelp')}
          </p>
        </div>

        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label>{t('common.name')}</Label>
            <Input value={user?.name || '—'} disabled className="bg-muted/50" />
          </div>
          <div className="space-y-1.5">
            <Label>{t('common.email')}</Label>
            <Input value={user?.email || '—'} disabled className="bg-muted/50" />
          </div>
          <div className="space-y-1.5">
            <Label>{t('common.role')}</Label>
            <Input value={roleLabel(user?.role, t)} disabled className="bg-muted/50" />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConfiguracoesModal({ open, onOpenChange }) {
  const { t } = useLanguage();
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setPwForm({ current_password: '', new_password: '', confirm: '' });
  }, [open]);

  const salvarSenha = async () => {
    const cur = (pwForm.current_password || '').trim();
    const nw = (pwForm.new_password || '').trim();
    const cf = (pwForm.confirm || '').trim();
    if (!cur) return toast.error('Informe a senha atual');
    if (!nw || nw.length < 8) return toast.error('A nova senha deve ter pelo menos 8 caracteres');
    if (nw !== cf) return toast.error('As senhas não conferem');
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API_BASE_URL}/api/auth/change-password`,
        { current_password: cur, new_password: nw },
        { withCredentials: true },
      );
      toast.success(data?.message || 'Senha alterada com sucesso');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="modal-configuracoes">
        <OfficeModalHeader
          icon={KeyRound}
          title={t('account.changePassword')}
          description={t('account.settingsHint')}
        />
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label>{t('account.currentPassword')}</Label>
            <Input
              type="password"
              autoComplete="current-password"
              value={pwForm.current_password}
              onChange={(e) => setPwForm((p) => ({ ...p, current_password: e.target.value }))}
              placeholder={t('account.placeholderCurrent')}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('account.newPassword')}</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={pwForm.new_password}
              onChange={(e) => setPwForm((p) => ({ ...p, new_password: e.target.value }))}
              placeholder={t('account.placeholderNew')}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t('account.confirmNewPassword')}</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm((p) => ({ ...p, confirm: e.target.value }))}
              placeholder={t('account.placeholderConfirm')}
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={salvarSenha} disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('account.changePassword')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
